/**
 * CustomerProfile — view + edit the citizen's profile.
 *
 * Wired:
 *   - GET /v1/customers/:id  via useCustomerProfile()
 *   - PUT /v1/customers/:id  via updateCustomer() — fullName / displayName /
 *                            primaryEmail
 *
 * Linked (live elsewhere in the portal):
 *   - Address book CRUD      → /portal/address-change
 *   - KYC tier upgrade       → /nipost-pay/kyc
 *
 * Truly future work (no inline UI; surfaced as "contact support" affordances):
 *   - Phone change with OTP  — pending OTP delivery integration
 *   - Password change + MFA  — pending citizen self-service rollout
 */
import { useEffect, useState, type FormEvent, type JSX } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle2,
  Loader2,
  ShieldCheck,
  User,
} from 'lucide-react';

import { ApiError } from '../../lib/api-client';
import {
  updateCustomer,
  type CustomerResponse,
  type UpdateCustomerRequest,
} from '../../lib/api/customer';
import { useCustomerAuth } from '../../lib/auth/useCustomerAuth';
import { PageHeader, StatusBadge } from './PortalUI';
import {
  customerProfileQueryKey,
  useCustomerProfile,
} from './useCustomerProfile';

interface FormState {
  readonly fullName: string;
  readonly displayName: string;
  readonly primaryEmail: string;
}

function buildFormState(profile: CustomerResponse | undefined): FormState {
  return {
    fullName: profile?.fullName ?? '',
    displayName: profile?.displayName ?? '',
    primaryEmail: profile?.primaryEmail ?? '',
  };
}

export function CustomerProfile(): JSX.Element {
  const queryClient = useQueryClient();
  const { token, claims } = useCustomerAuth();
  const profile = useCustomerProfile();

  const [form, setForm] = useState<FormState>(() => buildFormState(profile.data));
  const [savedFlash, setSavedFlash] = useState<boolean>(false);

  // Hydrate the form whenever fresh profile data arrives.
  useEffect(() => {
    if (profile.data) {
      setForm(buildFormState(profile.data));
    }
  }, [profile.data]);

  const mutation = useMutation<
    CustomerResponse,
    Error,
    UpdateCustomerRequest
  >({
    mutationFn: (body) => {
      if (!claims?.customerId) {
        throw new Error('Missing customer id in JWT.');
      }
      return updateCustomer(claims.customerId, body, { bearerToken: token ?? undefined });
    },
    onSuccess: (next) => {
      if (claims?.customerId) {
        queryClient.setQueryData(customerProfileQueryKey(claims.customerId), next);
      }
      setSavedFlash(true);
      window.setTimeout(() => setSavedFlash(false), 2500);
    },
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (!profile.data) {
      return;
    }
    const body: { -readonly [K in keyof UpdateCustomerRequest]: UpdateCustomerRequest[K] } = {};
    if (form.fullName !== profile.data.fullName) {
      body.fullName = form.fullName.trim();
    }
    if ((profile.data.displayName ?? '') !== form.displayName) {
      const trimmed = form.displayName.trim();
      if (trimmed.length > 0) {
        body.displayName = trimmed;
      }
    }
    if ((profile.data.primaryEmail ?? '') !== form.primaryEmail) {
      const trimmed = form.primaryEmail.trim();
      body.primaryEmail = trimmed.length > 0 ? trimmed : null;
    }
    if (Object.keys(body).length === 0) {
      return;
    }
    mutation.mutate(body);
  }

  const kycLevel = profile.data?.kycLevel ?? 'NONE';
  const kycLevels: readonly {
    readonly level: 'BASIC' | 'STANDARD' | 'ENHANCED';
    readonly limits: string;
    readonly requirements: string;
  }[] = [
    { level: 'BASIC',    limits: 'Send up to 5kg, ₦50,000/day',          requirements: 'Phone verification' },
    { level: 'STANDARD', limits: 'Send up to 20kg, ₦500,000/day',        requirements: 'NIN + selfie' },
    { level: 'ENHANCED', limits: 'Unlimited weight, ₦5,000,000/day',     requirements: 'In-person verification' },
  ];
  const currentTierLabel = kycLevel === 'NONE' ? 'BASIC' : kycLevel;

  const errorMessage = (() => {
    if (!mutation.error) {
      return null;
    }
    if (mutation.error instanceof ApiError) {
      if (mutation.error.status === 401 || mutation.error.status === 403) {
        return 'Your session has expired. Please sign in again.';
      }
      return 'We couldn’t save your changes. Please try again.';
    }
    return 'We couldn’t save your changes. Please try again.';
  })();

  return (
    <div>
      <PageHeader title="My Profile" description="Manage your account settings and preferences" />

      {profile.isError ? (
        <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800" role="alert">
          We couldn&apos;t load your profile: <span className="font-mono">{profile.error.message}</span>
        </div>
      ) : null}

      <div className="space-y-6">
        {/* Personal Information — wired */}
        <div className="bg-white rounded-xl border border-border p-6">
          <div className="flex items-center gap-2 mb-5">
            <User className="w-5 h-5 text-nipost-green" />
            <h2 className="font-semibold text-gray-900">Personal Information</h2>
          </div>

          {profile.isLoading ? (
            <p className="text-sm text-muted">Loading…</p>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5" noValidate>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="profile-fullName" className="block text-xs font-medium text-muted mb-1.5">
                    Full Name
                  </label>
                  <input
                    id="profile-fullName"
                    type="text"
                    value={form.fullName}
                    onChange={(e) => setForm((s) => ({ ...s, fullName: e.target.value }))}
                    className="w-full px-3 py-2.5 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-nipost-green/20 focus:border-nipost-green"
                  />
                </div>
                <div>
                  <label htmlFor="profile-displayName" className="block text-xs font-medium text-muted mb-1.5">
                    Display Name
                  </label>
                  <input
                    id="profile-displayName"
                    type="text"
                    value={form.displayName}
                    onChange={(e) => setForm((s) => ({ ...s, displayName: e.target.value }))}
                    className="w-full px-3 py-2.5 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-nipost-green/20 focus:border-nipost-green"
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <label htmlFor="profile-email" className="block text-xs font-medium text-muted mb-1.5">
                    Email Address
                  </label>
                  <input
                    id="profile-email"
                    type="email"
                    value={form.primaryEmail}
                    onChange={(e) => setForm((s) => ({ ...s, primaryEmail: e.target.value }))}
                    className="w-full px-3 py-2.5 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-nipost-green/20 focus:border-nipost-green"
                  />
                </div>
                <div>
                  <label htmlFor="profile-phone" className="block text-xs font-medium text-muted mb-1.5">
                    Phone Number
                  </label>
                  <input
                    id="profile-phone"
                    type="text"
                    value={profile.data?.primaryPhone ?? ''}
                    readOnly
                    className="w-full px-3 py-2.5 text-sm border border-border rounded-lg bg-gray-50 text-muted"
                  />
                  <p className="text-[11px] text-muted mt-1">
                    To change your phone number, please contact NIPOST support — phone
                    updates require OTP verification.
                  </p>
                </div>
                <div>
                  <label htmlFor="profile-customerNumber" className="block text-xs font-medium text-muted mb-1.5">
                    Customer Number
                  </label>
                  <input
                    id="profile-customerNumber"
                    type="text"
                    value={profile.data?.customerNumber ?? ''}
                    readOnly
                    className="w-full px-3 py-2.5 text-sm border border-border rounded-lg bg-gray-50 text-muted font-mono"
                  />
                </div>
              </div>

              {errorMessage ? (
                <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700" role="alert">
                  {errorMessage}
                </div>
              ) : null}

              {savedFlash ? (
                <div
                  className="rounded-lg bg-success-light border border-success/20 px-3 py-2 text-xs text-success flex items-center gap-2"
                  role="status"
                >
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Profile updated.
                </div>
              ) : null}

              <button
                type="submit"
                disabled={mutation.isPending || profile.isLoading}
                className="px-6 py-2.5 bg-nipost-green text-white text-sm font-medium rounded-lg hover:bg-nipost-green-dark transition-colors disabled:opacity-60 inline-flex items-center gap-2"
              >
                {mutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Save Changes
              </button>
            </form>
          )}
        </div>

        {/* KYC Status — read-only view from the live profile */}
        <div className="bg-white rounded-xl border border-border p-6">
          <div className="flex items-center gap-2 mb-5">
            <ShieldCheck className="w-5 h-5 text-nipost-green" />
            <h2 className="font-semibold text-gray-900">KYC Status</h2>
          </div>

          <div className="flex items-center gap-3 mb-4">
            <span className="text-sm text-muted">Current Level:</span>
            <StatusBadge status={kycLevel === 'NONE' ? 'PENDING' : 'VERIFIED'} />
            <span className="text-sm font-semibold text-gray-900">{currentTierLabel}</span>
          </div>

          <h3 className="text-sm font-medium text-gray-900 mb-3">Verification Levels</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {kycLevels.map((level) => {
              const isCurrent = level.level === currentTierLabel;
              return (
                <div
                  key={level.level}
                  className={`p-4 rounded-xl border ${
                    isCurrent ? 'border-nipost-green bg-nipost-green-light' : 'border-border'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="text-sm font-semibold text-gray-900">{level.level}</h4>
                    {isCurrent ? <CheckCircle2 className="w-4 h-4 text-nipost-green" /> : null}
                  </div>
                  <p className="text-xs text-gray-600 mb-2">{level.limits}</p>
                  <p className="text-[10px] text-muted">Requires: {level.requirements}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-4">
            <Link
              to="/nipost-pay/kyc"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark transition-colors"
            >
              Upgrade KYC tier
            </Link>
          </div>
        </div>

        {/* Address book — wired via /portal/address-change */}
        <div className="bg-white rounded-xl border border-border p-6">
          <h2 className="font-semibold text-gray-900 mb-2">Address Book</h2>
          <p className="text-sm text-muted mb-3">
            Manage saved pickup and delivery addresses so future bookings auto-fill.
          </p>
          <Link
            to="/portal/address-change"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark transition-colors"
          >
            Manage addresses
          </Link>
        </div>

        {/* Password / MFA — pending citizen self-service rollout */}
        <div className="bg-white rounded-xl border border-border p-6">
          <h2 className="font-semibold text-gray-900 mb-2">Password &amp; Security</h2>
          <p className="text-sm text-muted">
            To change your password, use the {' '}
            <Link to="/auth/forgot-password" className="text-nipost-green hover:underline">
              password reset
            </Link>
            {' '} flow. Two-factor authentication for citizen accounts is on the roadmap.
          </p>
        </div>
      </div>
    </div>
  );
}
